<template>
  <div class="social-sharing">
    <fade-transition>
      <a-visibility v-if="isSocialListOpened" show :on="[$breakpoint.mobile]">
        <div class="social-sharing__overlay" />
      </a-visibility>
    </fade-transition>

    <collapse-transition>
      <div
        v-if="isSocialListOpened"
        ref="socialSharingContainer"
        v-on-clickaway="closeSocialListAndFocusActivator"
        class="social-sharing__container"
        :class="containerClass"
      >
        <a-social-sharing
          v-for="social in socialSharingList"
          :key="social.icon"
          class="social-sharing__icon-container"
          hashtags="finance_magnates,fm"
          twitter-user="finance_magnates"
          :url="sharingUrl"
          :network="social.network"
          :title="getTitleBySocialType(social.network)"
          :description="getDescriptionBySocialType(social.network)"
          :quote="title"
          :open-in-iframe="openInIframe"
          @blur="onElementBlur"
          @open="closeSocialListAndFocusActivator"
          @hook:mounted="onSocialSharingMounted"
        >
          <a-icon
            :aria-label="social.ariaLabel"
            :title="social.title"
            width="auto"
            :height="social.size"
            :color="social.color"
            :original="social.original"
            :icon="social.icon"
            class="social-icon"
          />
        </a-social-sharing>

        <div class="social-sharing__features">
          <span class="social-sharing__title hide-on-desktop">Share</span>
          <div class="social-sharing__close-container">
            <div
              v-focusable
              class="social-sharing__close-icon close-icon close-icon_sm"
              :class="closeIconClass"
              @click="closeSocialListAndFocusActivator"
              @blur="onElementBlur"
            />
          </div>
        </div>
      </div>
    </collapse-transition>
  </div>
</template>

<script>
import { directive as onClickaway } from 'vue-clickaway'

import { ICON, ICON_COLOR } from 'shared/AIcon'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import socialSharing from '../../socialSharing'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { NETWORK } from 'shared/AActionIcon/Share/SocialSharingList/enums'
import { getFirstFocusableDescendant, hasParentWith } from '@fmpedia/helpers'

const NETWORK_LIST = Object.values(NETWORK)

const SOCIAL_SHARING_DESKTOP = {
  [NETWORK.TWITTER]: { icon: ICON.TWITTER, title: 'Twitter' },
  [NETWORK.LINKED_IN]: { icon: ICON.LINKEDIN, title: 'LinkedIn' },
  [NETWORK.TELEGRAM]: { icon: ICON.TELEGRAM, title: 'Telegram' },
  [NETWORK.FACEBOOK]: { icon: ICON.FACEBOOK, title: 'Facebook' },
  [NETWORK.WHATS_APP]: { icon: ICON.WHATSUP_ROUND_GREEN, title: 'WhatsApp' },
  [NETWORK.EMAIL]: { icon: ICON.ENVELOPE_2, title: 'Email' },
  [NETWORK.REDDIT]: { icon: ICON.REDDIT_ROUND_ORANGE, title: 'Reddit' }
}

const SOCIAL_SHARING_MOBILE = {
  [NETWORK.TWITTER]: { icon: ICON.SOCIAL_SQUARE_X, title: 'Twitter' },
  [NETWORK.LINKED_IN]: { icon: ICON.SOCIAL_SQUARE_LINKEDIN, title: 'LinkedIn' },
  [NETWORK.TELEGRAM]: { icon: ICON.SOCIAL_SQUARE_TELEGRAM, title: 'Telegram' },
  [NETWORK.FACEBOOK]: { icon: ICON.SOCIAL_SQUARE_FACEBOOK, title: 'Facebook' },
  [NETWORK.WHATS_APP]: { icon: ICON.SOCIAL_SQUARE_WHATSAPP, title: 'WhatsApp' },
  [NETWORK.EMAIL]: { icon: ICON.SOCIAL_SQUARE_EMAIL, title: 'Email' },
  [NETWORK.REDDIT]: { icon: ICON.SOCIAL_SQUARE_REDDIT, title: 'Reddit' }
}

export default {
  name: 'SocialSharingList',
  directives: { onClickaway },
  mixins: [socialSharing, hydrationHelpers],
  model: {
    prop: 'isSocialListOpened'
  },
  props: {
    isSocialListOpened: propValidator([PROP_TYPES.BOOLEAN]),
    heightAuto: propValidator([PROP_TYPES.BOOLEAN]),
    networkList: propValidator(
      [PROP_TYPES.ARRAY],
      false,
      () => NETWORK_LIST,
      arr => arr.every(network => NETWORK_LIST.includes(network))
    ),
    networkListMobile: propValidator(
      [PROP_TYPES.ARRAY],
      false,
      () => NETWORK_LIST,
      arr => arr.every(network => NETWORK_LIST.includes(network))
    ),
    openInIframe: propValidator([PROP_TYPES.BOOLEAN])
  },
  data() {
    return {
      isSocialSharingLazyLoaded: false
    }
  },
  computed: {
    socialSharingList() {
      const isMobile = this.$_hydrationHelpers_isLayoutMobile
      const networkList = isMobile ? this.networkListMobile : this.networkList
      const networkProps = isMobile
        ? SOCIAL_SHARING_MOBILE
        : SOCIAL_SHARING_DESKTOP
      const styleProps = isMobile
        ? { size: 25, original: true }
        : { size: 18, original: false, color: ICON_COLOR.WHITE }

      return networkList.map(network => ({
        ...networkProps[network],
        ...styleProps,
        network,
        ariaLabel: network
      }))
    },
    closeIconClass() {
      return {
        'close-icon_white': !this.$_hydrationHelpers_isLayoutMobile
      }
    },
    containerClass() {
      return { 'social-sharing__container_auto-height': this.heightAuto }
    },
    setFocusDataTrigger() {
      return this.isSocialListOpened && this.isSocialSharingLazyLoaded
    }
  },
  watch: {
    async setFocusDataTrigger(newVal) {
      if (!newVal) return

      await this.$nextTick()

      const focusableChild = getFirstFocusableDescendant(
        this.$refs.socialSharingContainer
      )

      if (focusableChild) {
        focusableChild.focus()
      }
    }
  },
  methods: {
    onSocialSharingMounted() {
      this.isSocialSharingLazyLoaded = true
    },
    onElementBlur(event) {
      const { relatedTarget } = event

      if (
        relatedTarget &&
        !hasParentWith(event.relatedTarget, {
          refEl: this.$refs.socialSharingContainer
        })
      ) {
        this.closeSocialList()
      }
    },
    closeSocialListAndFocusActivator() {
      this.closeSocialList()
      this.$emit('focus-activator')
    },
    closeSocialList() {
      this.$emit('input', false)
    },
    getTitleBySocialType(type) {
      if (type === NETWORK.EMAIL && this.emailTitle) {
        return this.emailTitle
      }

      return this.title
    },
    getDescriptionBySocialType(type) {
      if (type === NETWORK.EMAIL && this.emailDescription) {
        return this.emailDescription
      }

      return this.description
    }
  }
}
</script>

<style lang="scss" scoped>
$mobile-padding-left: 30px;
$mobile-title-height: 44px;

$social-sharing-gap: 16px;

$overlay-z-index: 1001;
$container-z-index: $overlay-z-index + 1;

.social-sharing {
  &__container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 263px;
    padding: 25px 20px 20px;
    background-color: $c--main;
    z-index: $z-index-share-social;

    @include mobile {
      display: block;
      position: fixed;
      height: auto;
      width: 100%;
      top: auto;
      bottom: 0;
      padding: 0;
      border: 2px solid $c--main;
      background-color: white;
      z-index: $container-z-index;
    }

    /deep/ .social-icon {
      flex-direction: row;
      width: 100%;

      .a-icon__icon {
        margin-right: 10px;
      }

      .a-icon__description {
        margin-top: 0;
        font-size: 14px;
        font-weight: 600;
      }

      .a-icon__description::before {
        margin: 0;
        text-align: left;
      }
    }

    &_auto-height {
      height: auto;
    }
  }

  &__icon-container {
    display: flex;
    align-items: flex-start;
    width: calc(50% - #{$social-sharing-gap / 2});
    margin-bottom: $social-sharing-gap;

    &:nth-last-child(1),
    &:nth-last-child(2) {
      margin-bottom: 0;
    }

    @include mobile {
      width: 100%;
      margin: 0;
      padding: 10px $mobile-padding-left;
      border-bottom: 2px solid #ebebeb;
    }
  }

  &__title {
    font-size: 19px;

    font-weight: bold;
    line-height: 20px;
  }

  &__features {
    position: absolute;
    top: 0;
    right: 0;

    @include mobile {
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: $mobile-title-height;
      padding: 10px 10px 10px $mobile-padding-left;
      background-color: rgba(0, 0, 0, 0.1);
    }
  }

  &__close-container {
    position: absolute;
    background-color: $c--black;
    top: 0;
    right: 0;
    width: 22px;
    height: 22px;

    /deep/ .close-icon {
      top: 3px;
      left: 3px;
    }

    @include mobile {
      position: relative;
      background-color: transparent;
    }
  }

  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: $overlay-z-index;
    background-color: rgba(0, 0, 0, 0.4);
  }
}
</style>
